import React, { useState, useRef, useEffect, useContext } from 'react';
import cn from 'clsx';
import { string, bool } from 'prop-types';
import { AutocompleteContext } from '@immediate_media/component-config';
import { useTranslation } from 'react-i18next';
import Form from '../../organisms/Form/index.jsx';
import Button from '../Button/index.jsx';
import IconStack from '../IconStack/index.jsx';
import Icon from '../../atoms/Icon/index.jsx';

/**
 * SearchForm component.
 */
var _ref = /*#__PURE__*/React.createElement(Icon, {
  className: "search-form__search-icon",
  name: "search",
  color: "clickableRegular",
  size: 22
});
var _ref2 = /*#__PURE__*/React.createElement(IconStack, null, /*#__PURE__*/React.createElement(IconStack.Icon, {
  name: "cross",
  color: "baseWhite",
  size: 8
}), /*#__PURE__*/React.createElement(IconStack.Icon, {
  name: "circle",
  color: "backgroundExtraDark",
  size: 16
}));
var _ref3 = /*#__PURE__*/React.createElement(Form.Label, {
  className: "sr-only"
}, "Search");
const SearchForm = ({
  className,
  url,
  inverted,
  mobileSquareBorders,
  placeholder,
  query,
  inputId,
  focusInput,
  isAutocomplete,
  ...restProps
}) => {
  const formRef = useRef(null);
  const searchInputRef = useRef(null);
  const [value, setValue] = useState('');
  const {
    t
  } = useTranslation();
  const autocomplete = useContext(AutocompleteContext);
  let defaultValue = '';
  useEffect(() => {
    // Note that iOS devices only want to show the keyboard on user-interaction to
    // an input element. The focus function will not cause the keyboard to show,
    // although it will cause the styling to change. Rather than hacking our
    // way around this we choose to just not trigger the event at all on iPad and iPhone
    if (focusInput) {
      if (typeof navigator !== 'undefined' && !navigator.userAgent.match(/iPad|iPhone/i)) {
        searchInputRef.current.focus();
      }
    }
  });
  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    defaultValue = currentUrl.searchParams.get(query);
    searchInputRef.current.value = defaultValue;
    setValue(defaultValue);
  }, []);
  const prepend = _ref;
  const append = /*#__PURE__*/React.createElement(Button, {
    className: 'search-form__reset-button' + (!value ? " search-form--hidden" : ""),
    inputAppend: true,
    ariaLabel: t('molecules.searchForm.ariaLabel.resetButton'),
    type: "reset",
    transparent: true,
    onClick: () => {
      setValue('');
      searchInputRef.current.focus();
    }
  }, _ref2);
  const onChange = e => {
    setValue(e.currentTarget.value);
  };
  return /*#__PURE__*/React.createElement(Form, {
    className: cn('search-form', className),
    action: url,
    inverted: inverted,
    mobileSquareBorders: mobileSquareBorders,
    role: "search",
    method: "GET",
    ref: formRef,
    "data-placement": restProps.dataPlacement,
    "data-component": "SearchForm"
  }, /*#__PURE__*/React.createElement(Form.Group, {
    inputId: inputId
  }, _ref3, /*#__PURE__*/React.createElement(Form.InputGroup, {
    className: "chromatic-ignore",
    defaultValue: defaultValue,
    placeholder: placeholder,
    name: query,
    autoComplete: "off",
    formInputRef: searchInputRef,
    prepend: prepend,
    append: append,
    onChange: onChange
  }, isAutocomplete && autocomplete ? /*#__PURE__*/React.createElement(Form.Autocomplete, {
    autocomplete: autocomplete,
    className: "form-autocomplete__group"
  }) : null)));
};
SearchForm.defaultProps = {
  className: null,
  url: null,
  inverted: null,
  mobileSquareBorders: false,
  placeholder: 'Search...',
  query: null,
  inputId: 'search-field',
  focusInput: false,
  isAutocomplete: false
};
SearchForm.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** The URL for the search. */
  url: Form.propTypes.action,
  /** Use the inverted form style. */
  inverted: Form.propTypes.inverted,
  /** Use to add 0 border radius to all input for tablet down break point. */
  mobileSquareBorders: bool,
  /** The placeholder for the search input. */
  placeholder: string,
  /** The query parameter used for the search. */
  query: string,
  /** An ID for the search input (defaults to "search-field"). */
  inputId: string,
  /** Focus input search field */
  focusInput: bool,
  /** Use the autocomplete search. */
  isAutocomplete: bool
} : {};
export default SearchForm;